import { call, put, select, takeLatest } from "redux-saga/effects";

import type {
  FiWeeklySummaryPresetListResponse,
  FiWordCloudPresetListResponse,
} from "@mapmycustomers/shared/types/entity/fieldInsight";
import type Organization from "@mapmycustomers/shared/types/Organization";
import type ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import { toBeSettings } from "@app/component/executiveSummary/util/wordCloudConfigConversion";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";
import type CalendarSyncAffectedUser from "@app/types/nylas/CalendarSyncAffectedUser";

import { toBePresetPayload } from "../component/ExecutiveSummaryPresets/util/presetConversion";

import {
  applyExecutiveSummaryPreset,
  applyWordCloudPreset,
  deleteExecutiveSummaryPreset,
  deleteWordCloudPreset,
  fetchAffectedUsers,
  fetchExecutiveSummaryPresets,
  fetchWordCloudPresets,
  refreshExecutiveSummaryPresets,
  saveExecutiveSummaryPreset,
  saveWordCloudPreset,
} from "./actions";

export function* onFetchAffectedUsers({
  payload: { callback, failureCallback, filter },
}: ReturnType<typeof fetchAffectedUsers.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: ListResponse<CalendarSyncAffectedUser> = yield callApi(
      "fetchCalendarSyncStatus",
      orgId,
      { $filters: filter, $limit: 100 }
    );
    yield put(fetchAffectedUsers.success({ total: response.total, users: response.data }));
    if (callback) {
      yield call(callback, response.total);
    }
  } catch (error) {
    yield put(fetchAffectedUsers.failure());
    if (failureCallback) {
      yield call(failureCallback);
    }
    yield put(handleError({ displayNotification: false, error }));
  }
}

// One read behind both actions: they differ only in what a failure is allowed to do to the
// section — see refreshExecutiveSummaryPresets.
function* readExecutiveSummaryPresets(
  action: typeof fetchExecutiveSummaryPresets | typeof refreshExecutiveSummaryPresets,
  displayNotification: boolean
) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: FiWeeklySummaryPresetListResponse = yield callApi(
      "fetchFieldInsightsWeeklySummaryPresets",
      orgId
    );
    yield put(action.success({ organizationId: orgId, presets: response.data }));
  } catch (error) {
    yield put(action.failure());
    yield put(handleError({ displayNotification, error }));
  }
}

export function* onFetchExecutiveSummaryPresets() {
  yield* readExecutiveSummaryPresets(fetchExecutiveSummaryPresets, true);
}

export function* onRefreshExecutiveSummaryPresets() {
  yield* readExecutiveSummaryPresets(refreshExecutiveSummaryPresets, false);
}

// Two calls, not one: a PUT leaves cached summaries in place, so without the "keep" apply the
// preset reaches nobody until the cache expires. "keep" never touches anyone's own settings.
export function* onSaveExecutiveSummaryPreset({
  payload: { config, roleKey },
}: ReturnType<typeof saveExecutiveSummaryPreset.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi(
      "upsertFieldInsightsWeeklySummaryPreset",
      orgId,
      toBePresetPayload(config, roleKey)
    );
    yield callApi("applyFieldInsightsWeeklySummaryPreset", orgId, roleKey, "keep");
    yield put(saveExecutiveSummaryPreset.success());
    yield put(fetchExecutiveSummaryPresets.request());
  } catch (error) {
    yield put(saveExecutiveSummaryPreset.failure());
    // The PUT may well have landed and only the apply failed, which leaves the cards showing the
    // old preset while the server holds the new one. Re-reading either way keeps the two in step,
    // so a re-edit starts from what is actually stored. Quietly, though: this failure has already
    // been reported, and a recovery that fails too must not add a second toast.
    yield put(refreshExecutiveSummaryPresets.request());
    yield put(handleError({ error }));
  }
}

export function* onDeleteExecutiveSummaryPreset({
  payload: { onDone, roleKey },
}: ReturnType<typeof deleteExecutiveSummaryPreset.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("deleteFieldInsightsWeeklySummaryPreset", orgId, roleKey);
    yield put(deleteExecutiveSummaryPreset.success());
    yield put(fetchExecutiveSummaryPresets.request());
    if (onDone) {
      yield call(onDone);
    }
  } catch (error) {
    yield put(deleteExecutiveSummaryPreset.failure());
    yield put(handleError({ error }));
  }
}

export function* onApplyExecutiveSummaryPreset({
  payload: { mode, onDone, roleKey },
}: ReturnType<typeof applyExecutiveSummaryPreset.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("applyFieldInsightsWeeklySummaryPreset", orgId, roleKey, mode);
    yield put(applyExecutiveSummaryPreset.success());
    // Coverage moves when personal rows are deleted, so the cards have to be re-read.
    yield put(fetchExecutiveSummaryPresets.request());
    if (onDone) {
      yield call(onDone);
    }
  } catch (error) {
    yield put(applyExecutiveSummaryPreset.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchWordCloudPresets() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: FiWordCloudPresetListResponse = yield callApi(
      "fetchFieldInsightsWordCloudPresets",
      orgId
    );
    yield put(fetchWordCloudPresets.success({ organizationId: orgId, presets: response.data }));
  } catch (error) {
    yield put(fetchWordCloudPresets.failure());
    yield put(handleError({ error }));
  }
}

export function* onSaveWordCloudPreset({
  payload: { callback, config, failureCallback, roleKey },
}: ReturnType<typeof saveWordCloudPreset>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("upsertFieldInsightsWordCloudPreset", orgId, {
      ...toBeSettings(config),
      roleKey,
    });
    yield call(callback);
    yield put(fetchWordCloudPresets.request());
  } catch (error) {
    yield put(handleError({ error }));
    yield call(failureCallback);
  }
}

export function* onDeleteWordCloudPreset({
  payload: { onDone, roleKey },
}: ReturnType<typeof deleteWordCloudPreset.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("deleteFieldInsightsWordCloudPreset", orgId, roleKey);
    yield put(deleteWordCloudPreset.success());
    yield put(fetchWordCloudPresets.request());
    yield call(onDone);
  } catch (error) {
    yield put(deleteWordCloudPreset.failure());
    yield put(handleError({ error }));
  }
}

export function* onApplyWordCloudPreset({
  payload: { onDone, roleKey },
}: ReturnType<typeof applyWordCloudPreset.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("applyFieldInsightsWordCloudPreset", orgId, roleKey, "clear");
    yield put(applyWordCloudPreset.success());
    yield put(fetchWordCloudPresets.request());
    yield call(onDone);
  } catch (error) {
    yield put(applyWordCloudPreset.failure());
    yield put(handleError({ error }));
  }
}

export function* organizationDetailsSaga() {
  yield takeLatest(fetchAffectedUsers.request, onFetchAffectedUsers);
  yield takeLatest(fetchExecutiveSummaryPresets.request, onFetchExecutiveSummaryPresets);
  yield takeLatest(refreshExecutiveSummaryPresets.request, onRefreshExecutiveSummaryPresets);
  yield takeLatest(saveExecutiveSummaryPreset.request, onSaveExecutiveSummaryPreset);
  yield takeLatest(deleteExecutiveSummaryPreset.request, onDeleteExecutiveSummaryPreset);
  yield takeLatest(applyExecutiveSummaryPreset.request, onApplyExecutiveSummaryPreset);
  yield takeLatest(fetchWordCloudPresets.request, onFetchWordCloudPresets);
  yield takeLatest(saveWordCloudPreset, onSaveWordCloudPreset);
  yield takeLatest(deleteWordCloudPreset.request, onDeleteWordCloudPreset);
  yield takeLatest(applyWordCloudPreset.request, onApplyWordCloudPreset);
}
